<script setup lang="ts">
import SwitchToggle from "@/components/SwitchToggle/SwitchToggle.vue";
import {reactive,computed,ref} from "vue";
import CustomButton from "@/components/Button/Button.vue";
import {useI18n} from "vue-i18n";
import {useHomepageStore} from "@/stores/homepageStore";
import * as Calendars from '@/composables/calendar';

const {t} = useI18n();


const homepageStore= useHomepageStore();
const useCalendar  = reactive(Calendars.useCalendar())


const props = defineProps({
  'tabindex':Number
})


const switchToggleOptions = computed(() => {
  const options = [
    { label: t('datePicker.dates'), value: 'Dates' },
    { label: t('datePicker.flexible'), value: 'Flexible' },
  ];
  console.log('Switch toggle options updated:', options);
  return options;
});

const range = ref({
  start: new Date(2020, 0, 6),
  end: new Date(2020, 0, 10),
});


function clickedParent() {
  homepageStore.current = 'date';
}

function onDayClick(val) {
  const date = new Date(val.date.getTime() - (val.date.getTimezoneOffset() * 60000))
  useCalendar.setDate(date)
  homepageStore.selections.dates = [useCalendar.fromDate, useCalendar.toDate]
}

const onClearDates = () => {
  useCalendar.clearDates();
  homepageStore.selections.dates = [useCalendar.fromDate, useCalendar.toDate]
  range.value = {
    start: null,
    end: null
  };

  console.log("Dates cleared:", range.value);
};


function onApply() {
  homepageStore.selections.dates = [useCalendar.fromDate, useCalendar.toDate]
  homepageStore.next()
}

</script>

<template>
  <div class="relative w-1/2 h-full"
       :tabindex="props.tabindex">
    <slot name="display" :calendar="useCalendar" :clickedParent="clickedParent">
      <div id="when"
           class="flex flex-col justify-between h-full w-full py-4.5 pl-7.5 cursor-pointer"
           @click="clickedParent">
        <div
            class="when-container-content transition-all text-black/65 flex flex-row items-center gap-2  text-sm">
          <img src="@/assets/icons/svg/Calendar_16x16.svg" alt="Destination">
          <span>{{ t('datePicker.when') }}</span>
        </div>
        <div v-if="useCalendar.selectedOption === 'Flexible'">
          <div class="text-black  responsive-body-small flex flex-row items-center">
            <div class="mr-6">{{ useCalendar.selectedFlexiblePeriod }}</div>
          </div>
        </div>
        <div class="text-black responsive-title-5 flex flex-row items-center">
          <div class="mr-6">{{ useCalendar.startString || t('booking.selectDate') }}</div>
          <div v-if="useCalendar.fromDate !== null && useCalendar.toDate !== null" class="end-date-container relative mr-6 h-full w-0.5"/>
          <div>{{ useCalendar.endString }}</div>
        </div>
      </div>

    </slot>

    <transition name="slight-scroll">
      <div v-if="homepageStore.current == 'date'">
        <div
            class="border-t-2 border-primary-blue shadow-2xl absolute top-full w-full h-fit left-0 bg-white flex flex-col justify-center items-center pt-5 z-20 rounded-b-11xl">
          <SwitchToggle
              :options="switchToggleOptions.map(option => option.label)"
              :value="switchToggleOptions.find(option => option.value === useCalendar.selectedOption)?.label"
              @update:value="val => {const selectedOption = switchToggleOptions.find(option => option.label === val);
                if (selectedOption) {
                  useCalendar.selectedOption = selectedOption.value;}
                else {
                  console.error('Selected option not found for value:', val);}
              }"
          />
          <VDatePicker v-if="useCalendar.selectedOption === 'Dates'"
              v-model.range.number="range"
              :attributes="useCalendar.dateRange"
              borderless
              @mouseover="onMouseOver"
              :columns="2"
              class="datepicker-calendar"
              :min-date="new Date()"
              @dayclick="onDayClick"
              :firstDayOfWeek=2 />
          
          <div v-if="useCalendar.selectedOption === 'Dates'" class="herew flex-grow-0 w-full">
            <div class="w-full flex justify-center responsive-body-small px-5 items-start gap-1" v-if="homepageStore.warning && homepageStore.warning !=''">
              <img src="@/assets/icons/svg/Action_required_16x16.svg" :alt="t('info')" class="mt-0.5 flex-shrink-0"/>
              <span>{{ homepageStore.warning }}</span>
            </div>
            <div class="w-full">
            <div
                class="from-gradients-ghostWhite to-gradients-aliceBlue bg-gradient-to-tr w-full mt-5 py-4.5 px-5 flex justify-between rounded-b-11xl test">
              <CustomButton class="hover:bg-black hover:text-white hover:border-black"
                            @click="onClearDates"
                            :disabled="!useCalendar.dateRange[0].dates.length"
                            type="black"
                            :outlined="false"
                            :rounded="true">
                {{ t('datePicker.clearDates') }}
              </CustomButton>
              <CustomButton class="hover:bg-black hover:text-white hover:border-black"
                            @click="onApply()"
                            :disabled="!homepageStore.canSearch"
                            type="black"
                            :outlined="false"
                            :rounded="true">
                {{ t('common.next') }}
              </CustomButton>
            </div>
            </div>
          </div>
          <div v-else class="flex flex-col justify-center items-center w-full">
            <div class="flex flex-col justify-center items-center mt-7.5 gap-x-1.5">
              <div class="responsive-body-small font-semibold">{{ t('datePicker.duration') }}</div>
              <div class="flex items-center gap-1.5 mt-2 cursor-pointer">
                <VChip :variant="useCalendar.selectedFlexiblePeriod === '7 days' ? 'flat' : 'outlined'"
                       @click="useCalendar.selectedFlexiblePeriod = '7 days'; useCalendar.howManyWeeks = 1">{{ t('datePicker.7days') }}
                </VChip>
                <VChip :variant="useCalendar.selectedFlexiblePeriod === '2 weeks' ? 'flat' : 'outlined'"
                       @click="useCalendar.selectedFlexiblePeriod = '2 weeks'; useCalendar.howManyWeeks = 2">{{ t('datePicker.2weeks') }}
                </VChip>
                <VChip :variant="useCalendar.selectedFlexiblePeriod === '3 weeks' ? 'flat' : 'outlined'"
                       @click="useCalendar.selectedFlexiblePeriod = '3 weeks'; useCalendar.howManyWeeks = 3">{{ t('datePicker.3weeks') }}
                </VChip>
                <VChip :variant="useCalendar.selectedFlexiblePeriod === '1 month' ? 'flat' : 'outlined'"
                       @click="useCalendar.selectedFlexiblePeriod = '1 month'; useCalendar.howManyWeeks = 4">{{ t('datePicker.1month') }}
                </VChip>
              </div>
            </div>
            <div class="flex flex-col justify-center items-center mt-7.5 gap-1.5">
              <div class="responsive-body-small font-semibold">{{ t('datePicker.months') }}</div>
              <VSheet max-width="400" class="mt-2">
                <VSlideGroup>
                  <VSlideGroupItem v-for="month in useCalendar.sortedMonths" :key="month.id">
                    <div @click="useCalendar.setMonth(month);"
                         :class="`flex justify-center transition-all month items-center
                          w-20 h-16
                          bg-background-light
                          mx-1 rounded-2xl cursor-pointer
                          ${useCalendar.selectedFlexibleMonth?.id == month.id ? 'border-[1px] border-primary-blue' : ''}`">
                      {{ month.name }}
                    </div>
                  </VSlideGroupItem>
                </VSlideGroup>
              </VSheet>
            </div>
            <div
                class="from-gradients-ghostWhite to-gradients-aliceBlue bg-gradient-to-tr w-full mt-5 py-4.5 px-5 flex justify-between rounded-b-11xl">
              <CustomButton class="hover:bg-black hover:text-white hover:border-black"
                            @click="onClearDates"
                            :disabled="!useCalendar.dateRange[0].dates.length"
                            type="black"
                            :outlined="false"
                            :rounded="true">
                {{ t('datePicker.clearDates') }}
              </CustomButton>
              <CustomButton class="hover:bg-black hover:text-white hover:border-black"
                            @click="onApply()"
                            :disabled="!useCalendar.dateRange[0].dates.length"
                            :outlined="false"
                            type="black"
                            :rounded="true">
                {{ t('common.next') }}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>


:deep(.vc-disabled) {
  pointer-events: none;
}

:deep(.datepicker-calendar) {
  @apply scale-[0.85];
}
@media (max-width: 975px) {
  :deep(.datepicker-calendar) {
    @apply scale-[0.75];
  }
}
@media (max-width: 805px) {
  :deep(.datepicker-calendar) {
    @apply scale-[0.70];
  }
}

:deep(.vc-weekday) {
  @apply text-black font-normal text-base;
}

:deep(.weekday-7 div) {
  @apply font-bold
}

:deep(.vc-weekday-7) {
  @apply font-bold
}

:deep(.vc-title) {
  @apply text-sm;
}

.elevated .when-container-content {
  font-size: .75rem;
}

.end-date-container::before {
  --chevron-width: 13px;
  content: url('@/assets/icons/svg/chevron.svg');
  position: absolute;
  left: calc(3px - var(--chevron-width));
  top: calc(50% - 5px);
  transform: translateX(50%) translateY(-50%) rotate(180deg) scale(1.25);
  width: var(--chevron-width);
  height: 100%;
}

@media (min-width: 1536px) {
  .elevated .when-container-content {
    font-size: 0.875rem;
  }
}

</style>
